import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";

import { faro, LogLevel } from "@grafana/faro-web-sdk";
import { checkIfTokenExpired } from "@vega-api/custom/utils/checkIfTokenExpired";
import { LocalStorageService } from "@vega-core/service/LocalStorage.service";
import { getBaseURL } from "@vega-core/utils/global";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

import {
  getLogoutInProgress,
  handleBffLogout,
  handleLogout,
  isOnLoginPage,
} from "./utils/logout";
import { isBffSessionActive } from "./utils/sessionCheck";

const exemptPaths = new Set(["/authentication/login"]);

export type AxiosRequestConfigWithRetry = AxiosError<unknown> & {
  config: InternalAxiosRequestConfig & {
    _retry?: boolean;
  };
};

function isAuthRejection(err: unknown): boolean {
  if (!axios.isAxiosError(err)) {
    return false;
  }

  const status = err.response?.status;

  return status === 401 || status === 403;
}

function reportUnauthorizedWithActiveSession(
  request: InternalAxiosRequestConfig,
  reason: string,
) {
  faro.api.pushLog(
    [`Received 401 but ${reason} - likely incorrect error mapping on backend`],
    {
      level: LogLevel.WARN,
      context: {
        type: "auth.401-with-active-session",
        reason,
        endpoint: request.url ?? "unknown",
        method: request.method?.toUpperCase() ?? "unknown",
        sessionId: faro.api.getSession()?.id ?? "unknown",
      },
    },
  );
}

export const errorInterceptor = async (error: AxiosRequestConfigWithRetry) => {
  if (
    isTruthy(error.config) &&
    isTruthy(error.config.url) &&
    exemptPaths.has(error.config.url)
  ) {
    return Promise.reject(error);
  }

  const originalRequest = error.config;

  if (
    isTruthy(error.response) &&
    error.response.status === 401 &&
    isTruthy(originalRequest) &&
    isFalsy(originalRequest._retry)
  ) {
    if (getLogoutInProgress() || isOnLoginPage()) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const refreshToken = LocalStorageService.getRefreshToken();
      const accessToken = LocalStorageService.getToken();

      if (isFalsy(refreshToken) && isFalsy(accessToken)) {
        if (await isBffSessionActive()) {
          reportUnauthorizedWithActiveSession(
            originalRequest,
            "BFF session is active",
          );

          return Promise.reject(error);
        }

        if (!getLogoutInProgress()) {
          await handleBffLogout({ sessionExpired: true });
        }

        return Promise.reject(error);
      }

      if (isFalsy(refreshToken)) {
        if (isTruthy(accessToken) && !checkIfTokenExpired(accessToken)) {
          reportUnauthorizedWithActiveSession(
            originalRequest,
            "access token is still valid",
          );

          return Promise.reject(error);
        }

        if (!getLogoutInProgress()) {
          await handleLogout();
        }

        return Promise.reject(error);
      }

      const response = await axios.post(
        `${getBaseURL()}/api/authentication/v1/refresh`,
        {
          refreshToken,
        },
      );

      const newAccessToken = response.data.data.accessToken;
      LocalStorageService.setToken(newAccessToken);
      originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

      return axios(originalRequest);
    } catch (err) {
      if (isAuthRejection(err)) {
        if (!getLogoutInProgress()) {
          await handleLogout();
        }
      }

      return Promise.reject(err);
    }
  }

  return Promise.reject(error);
};
